'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, ExternalLink, Megaphone, PartyPopper, Zap, Star, Bell, Pin, LucideIcon } from 'lucide-react';
import { useActiveAnnouncement } from '@/hooks/useAnnouncements';
import { Announcement, AnnouncementIcon, AnnouncementAccentColor, ANNOUNCEMENT_SHOW_DELAY_MS } from '@/types/announcements';

// =============================================================================
// ICON & COLOR MAPS
// =============================================================================

const ICON_MAP: Record<AnnouncementIcon, LucideIcon> = {
  gift: Gift,
  megaphone: Megaphone,
  party: PartyPopper,
  zap: Zap,
  star: Star,
  bell: Bell,
};

const ACCENT_STYLES: Record<AnnouncementAccentColor, { gradient: string; dot: string; shadow: string }> = {
  green: {
    gradient: 'linear-gradient(135deg, #E8A832, #10b981)',
    dot: '#E8A832',
    shadow: 'rgba(6, 204, 109, 0.35)',
  },
  blue: {
    gradient: 'linear-gradient(135deg, #E8A832, #f5c563)',
    dot: '#E8A832',
    shadow: 'rgba(232, 168, 50, 0.35)',
  },
  purple: {
    gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
    dot: '#8b5cf6',
    shadow: 'rgba(139, 92, 246, 0.35)',
  },
  amber: {
    gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    dot: '#f59e0b',
    shadow: 'rgba(245, 158, 11, 0.35)',
  },
};

const BULLET_COLORS = ['#E8A832', '#06b6d4', '#8b5cf6', '#f59e0b'];

// =============================================================================
// MARKDOWN BOLD PARSER
// =============================================================================

function parseBoldMarkdown(text: string): React.ReactNode {
  // Split by **bold** pattern
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** and render as bold
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// =============================================================================
// STORAGE KEYS
// =============================================================================

const STORAGE_KEY_PREFIX = 'eureka_announcement_dismissed_';

// =============================================================================
// COMPONENT
// =============================================================================

export default function AnnouncementIndicator() {
  const { announcement, loading, trackImpression, trackClick } = useActiveAnnouncement();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasAutoShown, setHasAutoShown] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Get storage key for this announcement
  const storageKey = announcement ? `${STORAGE_KEY_PREFIX}${announcement.id}` : '';

  // Check localStorage on mount
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !announcement || !storageKey) return;

    try {
      const dismissed = localStorage.getItem(storageKey) === 'true';
      setIsDismissed(dismissed);

      // Auto-show modal if not dismissed and hasn't auto-shown yet
      if (!dismissed && !hasAutoShown) {
        const timer = setTimeout(() => {
          setIsModalOpen(true);
          setHasAutoShown(true);
          trackImpression();
        }, ANNOUNCEMENT_SHOW_DELAY_MS);

        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.warn('[AnnouncementIndicator] localStorage unavailable');
    }
  }, [mounted, announcement, storageKey, hasAutoShown, trackImpression]);

  const handleDismiss = useCallback(() => {
    setIsModalOpen(false);
    
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, 'true');
      } catch (error) {
        // Silent fail
      }
    }
    
    setIsDismissed(true);
  }, [storageKey]);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    trackImpression();
  }, [trackImpression]);

  const handleCTAClick = useCallback(() => {
    if (!announcement) return;
    trackClick();
    window.open(announcement.ctaUrl, '_blank', 'noopener,noreferrer');
    handleDismiss();
  }, [announcement, trackClick, handleDismiss]);

  // Don't render anything if no announcement or still loading
  if (!mounted || loading || !announcement) {
    return null;
  }

  const IconComponent = ICON_MAP[announcement.icon] || Megaphone;
  const accentStyle = ACCENT_STYLES[announcement.accentColor] || ACCENT_STYLES.green;

  return (
    <>
      {/* Navbar Indicator Button */}
      <button
        onClick={handleOpenModal}
        className="relative p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
        aria-label="View announcement"
        title="View announcement"
      >
        <Pin className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        {/* Notification dot - only show if not dismissed */}
        {!isDismissed && (
          <span 
            className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: accentStyle.dot }}
          />
        )}
      </button>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm"
              onClick={handleDismiss}
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
              className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
            >
              <div 
                className="relative w-full max-w-md pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Glow effect */}
                <div 
                  className="absolute -inset-1 rounded-2xl blur-lg opacity-40 animate-pulse"
                  style={{ background: accentStyle.gradient }}
                />
                
                {/* Card */}
                <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-neutral-700">
                  {/* Top accent bar */}
                  <div className="h-1 w-full" style={{ background: accentStyle.gradient }} />
                  
                  {/* Close button */}
                  <button
                    onClick={handleDismiss}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center 
                               text-gray-400 dark:text-neutral-500 
                               hover:text-gray-600 dark:hover:text-white 
                               hover:bg-gray-100 dark:hover:bg-neutral-800 
                               rounded-full transition-colors z-10"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Content */}
                  <div className="px-6 py-8 text-center">
                    {/* Icon */}
                    <div className="relative inline-flex items-center justify-center mb-5">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center"
                        style={{ background: accentStyle.gradient }}
                      >
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {announcement.title}
                    </h2>

                    {/* Subtitle */}
                    {announcement.subtitle && (
                      <p className="text-gray-600 dark:text-neutral-400 text-sm mb-5">
                        {announcement.subtitle}
                      </p>
                    )}

                    {/* Bullet Points */}
                    {announcement.bulletPoints && announcement.bulletPoints.length > 0 && (
                      <div className="space-y-2 mb-6">
                        {announcement.bulletPoints.map((bullet, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-center gap-2 text-sm"
                            style={{ color: BULLET_COLORS[index % BULLET_COLORS.length] }}
                          >
                            <span 
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: BULLET_COLORS[index % BULLET_COLORS.length] }}
                            />
                            <span>{parseBoldMarkdown(bullet)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* CTA Button */}
                    <button
                      onClick={handleCTAClick}
                      className="group w-full py-3 px-6 text-white font-semibold rounded-xl 
                                 transition-all duration-200 flex items-center justify-center gap-2 
                                 transform hover:-translate-y-0.5"
                      style={{ 
                        background: accentStyle.gradient,
                        boxShadow: `0 4px 14px 0 ${accentStyle.shadow}`
                      }}
                    >
                      <span>{announcement.ctaText}</span>
                      <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </button>

                    {/* Dismiss link */}
                    {announcement.dismissText && (
                      <button
                        onClick={handleDismiss}
                        className="mt-4 text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-300 text-xs transition-colors"
                      >
                        {announcement.dismissText}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
